import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchMyLikes, type LikedItem, type LikedKind } from '../lib/queries';
import { Input, Select, Text } from './ui';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import './ProfileLikes.css';

/**
 * Profile > Likes tab. Everything the member has liked, across content types -
 * liked posts, favorited Saleroom lots, and liked Redesign Challenge designs -
 * in one searchable / filterable / sortable grid. Data comes from fetchMyLikes
 * (client-side union over post_likes + post_watchers + post_submission_likes).
 */

type FilterId = 'all' | LikedKind;
type SortId = 'recent' | 'oldest' | 'title';

const FILTERS: { id: FilterId; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'post', label: 'Posts' },
  { id: 'lot', label: 'Saleroom' },
  { id: 'submission', label: 'Redesign' },
];

/** Display fields per item, normalised across the three content types. The tag
 *  is the closet/drawer name (e.g. "Redesign Challenge"), with a sensible
 *  fallback when an item has no drawer. */
function present(item: LikedItem): { cover: string | null; title: string; to: string | null; tag: string } {
  const fallbackTag =
    item.kind === 'lot' ? 'Saleroom' : item.kind === 'submission' ? 'Redesign Challenge' : 'Post';
  const tag = item.closet ?? fallbackTag;
  if (item.kind === 'submission') {
    return {
      cover: item.submission?.image_url ?? null,
      title: item.parent?.title ?? 'Redesign Challenge',
      to: item.parent ? `/p/${item.parent.slug}` : null,
      tag,
    };
  }
  const p = item.post;
  const cover = p?.thumbnail_url ?? p?.media?.thumbnail_url ?? null;
  const title = item.kind === 'lot' ? p?.auction?.lot_name ?? p?.title ?? '' : p?.title ?? '';
  return { cover, title, to: p ? `/p/${p.slug}` : null, tag };
}

export function ProfileLikes({ userId }: { userId: string }) {
  const [items, setItems] = useState<LikedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<FilterId>('all');
  const [sort, setSort] = useState<SortId>('recent');

  useEffect(() => {
    const ctrl = new AbortController();
    setItems(null);
    setError(null);
    (async () => {
      try {
        const data = await fetchMyLikes(userId, ctrl.signal);
        if (ctrl.signal.aborted) return;
        setItems(data);
      } catch (err: any) {
        if (ctrl.signal.aborted || err?.name === 'AbortError') return;
        console.error('[profile-likes] fetch failed', err);
        setError(err?.message ?? 'Something broke loading your likes.');
      }
    })();
    return () => ctrl.abort();
  }, [userId]);

  const visible = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    const rows = items
      // Drop items we can't link to (e.g. a liked design whose parent post is
      // no longer published) - nothing to navigate to.
      .filter((it) => present(it).to !== null)
      .filter((it) => (filter === 'all' ? true : it.kind === filter))
      .filter((it) => (q === '' ? true : present(it).title.toLowerCase().includes(q)));
    const sorted = [...rows];
    if (sort === 'title') {
      sorted.sort((a, b) => present(a).title.localeCompare(present(b).title));
    } else if (sort === 'oldest') {
      sorted.sort((a, b) => a.likedAt.localeCompare(b.likedAt));
    } else {
      sorted.sort((a, b) => b.likedAt.localeCompare(a.likedAt));
    }
    return sorted;
  }, [items, query, filter, sort]);

  if (error) {
    return <EmptyState eyebrow="Error" headline="We couldn't load your likes." body={error} />;
  }

  if (!items) {
    return (
      <div className="likes__grid" aria-busy="true" aria-label="Loading your likes">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="likes__skel">
            <Skeleton variant="card" aspectRatio={4 / 5} />
            <Skeleton variant="line" width="70%" height={14} />
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <EmptyState
        eyebrow="Likes"
        headline="You haven't liked anything yet."
        body="Tap the heart on a post, a Saleroom lot, or a redesign and it'll collect here."
      />
    );
  }

  return (
    <div className="likes">
      <div className="likes__controls">
        <Input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your likes"
          aria-label="Search your likes"
          className="likes__search"
        />
        <div className="likes__selects">
          <Select
            value={filter}
            onChange={(e) => setFilter(e.target.value as FilterId)}
            aria-label="Filter by type"
          >
            {FILTERS.map((f) => (
              <option key={f.id} value={f.id}>{f.label}</option>
            ))}
          </Select>
          <Select value={sort} onChange={(e) => setSort(e.target.value as SortId)} aria-label="Sort">
            <option value="recent">Recent</option>
            <option value="oldest">Oldest</option>
            <option value="title">Title A-Z</option>
          </Select>
        </div>
      </div>

      <Text role="caption" muted className="likes__count">
        {visible.length} {visible.length === 1 ? 'item' : 'items'}
        {filter !== 'all' || query ? ` of ${items.length}` : ''}
      </Text>

      {visible.length === 0 ? (
        <EmptyState eyebrow="No matches" headline="Nothing matches that." body="Try a different search or filter." />
      ) : (
        <div className="likes__grid">
          {visible.map((it) => {
            const { cover, title, to, tag } = present(it);
            return (
              <Link key={`${it.kind}-${it.post?.id ?? it.submission?.id}`} to={to!} className="likes__card">
                <div className="likes__cover">
                  {cover ? (
                    <img src={cover} alt="" loading="lazy" />
                  ) : (
                    <div className="likes__cover-empty" aria-hidden="true" />
                  )}
                </div>
                <div className="likes__meta">
                  <p className="likes__title">{title}</p>
                  <span className="likes__tag">{tag}</span>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
